import { useState, useEffect, createContext, useContext } from 'react';
import { Link, NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import {
    LayoutDashboard, Package, ShoppingCart, Tag, GitBranch, TrendingUp,
    Users, BookOpen, Layers, Wallet, Radio, Truck, MessageCircle, ReceiptText,
    Crown, LogOut, Menu, X, ChevronRight, Store, ArrowLeft, Settings, Bike
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// ── Context so child pages can access the negocio data ───────────────────────
export const NegocioContext = createContext(null);
export const useNegocio = () => useContext(NegocioContext);

// ── Order notification context so AliadoOrdersPage can push badge counts up ──
export const OrderNotificationsContext = createContext({ newOrderCount: 0, setNewOrderCount: () => {} });
export const useOrderNotifications = () => useContext(OrderNotificationsContext);

// ── Nav item ─────────────────────────────────────────────────────────────────
const NavItem = ({ to, icon: Icon, label, end, onClick, badge }) => (
    <NavLink to={to} end={end} onClick={onClick}
        className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive
                ? 'bg-orange-50 text-orange-700 border-l-2 border-orange-500 pl-2 font-medium'
                : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`
        }>
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1">{label}</span>
        {badge > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-orange-600 text-white text-[10px] font-bold h-4 min-w-[1rem] px-1 leading-none">
                {badge > 99 ? '99+' : badge}
            </span>
        )}
        <ChevronRight className="h-3 w-3 opacity-40" />
    </NavLink>
);

const SectionLabel = ({ label }) => (
    <div className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
        {label}
    </div>
);

// ── Guard ─────────────────────────────────────────────────────────────────────
export function AliadoGuard({ children }) {
    const { role, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando…</div>;
    if (role !== 'user') return <Navigate to="/login" replace />;
    return children;
}

// ── Layout ────────────────────────────────────────────────────────────────────
export default function AliadoDashboardLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [negocio, setNegocio] = useState(null);
    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    const [newOrderCount, setNewOrderCount] = useState(0);

    useEffect(() => {
        api.get('/me/oknegocio')
            .then(({ data }) => setNegocio(data))
            .catch(() => setNegocio(null))
            .finally(() => setLoading(false));
    }, []);

    const closeMenu = () => setMenuOpen(false);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando negocio…</div>;
    }

    if (!negocio) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
                <Store className="h-12 w-12 text-muted-foreground" />
                <h2 className="text-xl font-bold">No tienes un OkNegocio</h2>
                <p className="text-muted-foreground text-sm max-w-xs">
                    Primero regístra tu negocio para acceder al panel de gestión.
                </p>
                <Button onClick={() => nav('/aliado')} className="bg-orange-600 hover:bg-orange-700">
                    Registrar mi negocio
                </Button>
            </div>
        );
    }

    const isFood = ['okfood', 'okrestaurante'].includes(negocio.super_category);

    const buildNav = (close) => (
        <nav className="space-y-0.5">
            <SectionLabel label="General" />
            <NavItem to="/aliado/dashboard" end icon={LayoutDashboard} label="Resumen" onClick={close} />
            <NavItem to="/aliado/dashboard/orders" icon={ShoppingCart} label="Órdenes" onClick={close} badge={newOrderCount} />
            <NavItem to="/aliado/dashboard/sales" icon={TrendingUp} label="Ventas" onClick={close} />

            <SectionLabel label="Catálogo" />
            <NavItem to="/aliado/dashboard/products" icon={Package} label="Productos" onClick={close} />
            <NavItem to="/aliado/dashboard/categories" icon={Tag} label="Categorías" onClick={close} />
            {isFood
                ? <NavItem to="/aliado/dashboard/cartas" icon={BookOpen} label="OkCartas (menú)" onClick={close} />
                : <NavItem to="/aliado/dashboard/pasillos" icon={Layers} label="OkPasillos (catálogo)" onClick={close} />
            }

            <SectionLabel label="Operación" />
            <NavItem to="/aliado/dashboard/branches" icon={GitBranch} label="Sucursales" onClick={close} />
            <NavItem to="/aliado/dashboard/squad" icon={Users} label="OkSquad (equipo)" onClick={close} />

            <SectionLabel label="Herramientas" />
            <NavItem to="/dashboard/wallet" icon={Wallet} label="OkWallet" onClick={close} />
            <NavItem to="/dashboard/livestream" icon={Radio} label="OkLive" onClick={close} />
            <NavItem to="/dashboard/envios" icon={Truck} label="OkEnvíos" onClick={close} />
            <NavItem to="/aliado/dashboard/okdelivery" icon={Bike} label="OkDelivery" onClick={close} />
            <NavItem to="/dashboard/messages" icon={MessageCircle} label="Mensajes" onClick={close} />
            <NavItem to="/dashboard/invoices" icon={ReceiptText} label="Facturas" onClick={close} />
            <NavItem to="/planes" icon={Crown} label="Mi plan" onClick={close} />
            <NavItem to="/aliado/dashboard/settings" icon={Settings} label="Mi negocio" onClick={close} />
        </nav>
    );

    const bizBlock = (
        <div className="pb-4 mb-2 border-b">
            <div className="flex items-center gap-3">
                {negocio.logo_url
                    ? <img src={negocio.logo_url} alt={negocio.name} className="h-10 w-10 rounded-full object-cover shrink-0 border" />
                    : <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center font-bold text-orange-600 shrink-0 text-lg">{negocio.name?.[0]?.toUpperCase()}</div>
                }
                <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{negocio.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{negocio.super_category}</div>
                </div>
            </div>
            {negocio.status === 'pending' && (
                <div className="mt-2 text-[10px] bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-2 py-1">
                    ⏳ Tu negocio está en revisión. Pronto será activado.
                </div>
            )}
        </div>
    );

    const logoutBtn = (
        <div className="mt-3 border-t pt-3">
            <button onClick={() => { logout(); nav('/'); closeMenu(); }}
                className="w-full flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive px-3 py-2 rounded-lg">
                <LogOut className="h-4 w-4" /> Cerrar sesión
            </button>
        </div>
    );

    return (
        <OrderNotificationsContext.Provider value={{ newOrderCount, setNewOrderCount }}>
        <NegocioContext.Provider value={negocio}>
            <div className="min-h-screen bg-orange-50/30">

                {/* Mobile top bar */}
                <div className="lg:hidden sticky top-0 z-40 bg-card/95 backdrop-blur border-b shadow-sm px-4 py-3 flex items-center justify-between">
                    <button onClick={() => setMenuOpen(true)} aria-label="Abrir menú"
                        className="h-9 w-9 flex items-center justify-center rounded-xl border bg-background hover:bg-secondary transition-colors">
                        <Menu className="h-5 w-5" />
                    </button>
                    <Link to="/okgo" className="text-sm font-bold tracking-tight text-orange-700">OkNegocios</Link>
                    <Link to={`/aliado/${negocio.slug}`} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded border">
                        Ver perfil
                    </Link>
                </div>

                {/* Mobile drawer */}
                {menuOpen && (
                    <>
                        <div className="lg:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={closeMenu} />
                        <div className="lg:hidden fixed left-0 top-0 bottom-0 z-50 w-72 bg-card shadow-2xl overflow-y-auto flex flex-col">
                            <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                                <span className="font-semibold text-sm">Panel del negocio</span>
                                <button onClick={closeMenu} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                {bizBlock}
                                {buildNav(closeMenu)}
                                {logoutBtn}
                            </div>
                        </div>
                    </>
                )}

                {/* Main layout */}
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6">
                    {/* Desktop top bar */}
                    <div className="hidden lg:flex items-center justify-between mb-6">
                        <Link to="/okgo" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                            <ArrowLeft className="h-4 w-4" /> OkNegocios
                        </Link>
                        <Link to={`/aliado/${negocio.slug}`}
                            className="text-xs border rounded-lg px-3 py-1.5 hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
                            Ver perfil público
                        </Link>
                    </div>

                    <div className="grid grid-cols-12 gap-6">
                        {/* Desktop sidebar */}
                        <aside className="hidden lg:block col-span-3">
                            <div className="rounded-2xl border bg-card p-4 sticky top-6">
                                {bizBlock}
                                {buildNav(() => {})}
                                {logoutBtn}
                            </div>
                        </aside>

                        {/* Content */}
                        <section className="col-span-12 lg:col-span-9">
                            <Outlet />
                        </section>
                    </div>
                </div>
            </div>
        </NegocioContext.Provider>
        </OrderNotificationsContext.Provider>
    );
}
